import React, { useState } from 'react'

import { getErrorMessage } from 'utils'
import { useSnackbar } from 'contexts'
import { useProducts } from 'hooks/api'
import { useCurrentCompany } from 'hooks'
import { useBulkAssignDefaultTag } from 'hooks/api/useTagInventory'

import { Button, Modal } from '@repo/ui'
import { SearchSelect } from 'components'

interface SetProductsForTagModalProps {
  open: boolean
  onClose: () => void
  refetch: () => void
  /** The tag shop product ID (UUID) to assign as default for selected products. */
  tagShopProductId: string
}

export const SetProductsForTagModal = (props: SetProductsForTagModalProps) => {
  const { open, onClose = () => {}, refetch, tagShopProductId } = props
  const { open: notification } = useSnackbar()
  const { company } = useCurrentCompany()

  const [selectedProducts, setSelectedProducts] = useState<any[]>([])

  const { data: productsData } = useProducts(
    { limit: 200, company_id: company?.id },
    { enabled: !!company?.id }
  )
  const allProducts = productsData?.data ?? []

  // Filter out products that already have this tag as default
  const availableProducts = allProducts.filter(
    p => p.default_tag_id !== tagShopProductId
  )

  const { mutate: bulkAssign, isPending: isAssigning } = useBulkAssignDefaultTag({
    onSuccess: (data) => {
      handleClose()
      refetch()
    },
    onError: error => {
      notification({
        type: 'error',
        title: getErrorMessage(error),
      })
    },
  })

  const handleSave = () => {
    if (!selectedProducts.length || !tagShopProductId) return
    bulkAssign({
      tagShopProductId,
      product_ids: selectedProducts.map(p => p.id),
    })
  }

  const handleClose = () => {
    setSelectedProducts([])
    onClose()
  }

  return (
    <Modal
      title="Assign products"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>

          <Button
            disabled={!selectedProducts.length || isAssigning}
            onClick={handleSave}
          >
            {isAssigning ? 'Saving...' : `Set default tag (${selectedProducts.length})`}
          </Button>
        </>
      }
    >
      {/* Single inline search-select (the VERA-455 SearchSelect pattern) — not the
          SearchFilter popover wrapper, which rendered a second nested search. */}
      <SearchSelect
        key={tagShopProductId}
        items={availableProducts}
        selected={selectedProducts}
        searchParams={{ fields: ['title', 'sku'], preview: 'products' }}
        showSelect
        showClear={false}
        onChange={(items) => setSelectedProducts(items)}
      />
    </Modal>
  )
}
